import dayjs from 'dayjs'

import 'common/services/dateService'
import type { SeriesUnit } from 'features/Series/api/series.types'

const DATE_FORMAT = 'YYYY-MM-DD'
const UNIT_DAYS: Record<SeriesUnit, number> = { day: 1, week: 7, month: 0 }

const STATUS_LABELS: Record<string, string> = {
  '-': 'None',
  '*': 'Reminder done',
  '+': 'Chart pulled',
  x: 'Canceled',
  '?': 'No show',
  '@': 'Arrived',
  '~': 'Arrived late',
  '!': 'Left w/o visit',
  '#': 'Ins/fin issue',
  '<': 'In exam room',
  '>': 'Checked out',
  $: 'Coding done',
  '%': 'Canceled < 24h',
  '^': 'Pending',
  AVM: 'AVM Confirmed',
  SMS: 'SMS Confirmed',
  EMAIL: 'EMAIL Confimed',
  CALL: 'Callback requested',
}

const WEEK_DAYS = 7
const MONTHS_PER_YEAR = 12

const isDateText = (date: unknown): date is string =>
  typeof date === 'string' && dayjs.utc(date, DATE_FORMAT).isValid()

// Adds n units to a YYYY-MM-DD string; a month step clamps to the last day of the month.
export const addUnitClamped = (date: string, unit: SeriesUnit, n: number): string => {
  if (!isDateText(date)) return date
  const start = dayjs.utc(date, DATE_FORMAT)
  if (unit !== 'month') return start.add(n * UNIT_DAYS[unit], 'day').format(DATE_FORMAT)
  const target = start.startOf('month').add(n, 'month')
  const day = Math.min(start.date(), target.daysInMonth())
  return target.date(day).format(DATE_FORMAT)
}

// Whole days from `from` to `to`, both YYYY-MM-DD; a missing date counts as 0.
export const daysBetween = (from: string, to: string): number =>
  isDateText(from) && isDateText(to)
    ? dayjs.utc(to, DATE_FORMAT).diff(dayjs.utc(from, DATE_FORMAT), 'day')
    : 0

// Wall-clock digits as written: 2026-11-14 → 11/14/2026 (M/D/YYYY); no date gives ''.
export const toUsDate = (date: string | null | undefined): string => {
  if (typeof date !== 'string') return ''
  const [year, month, day] = date.split('-')
  return year && month && day ? `${Number(month)}/${Number(day)}/${year}` : date
}

export const unitLabel = (n: number, unit: SeriesUnit): string =>
  `${n} ${unit}${n === 1 ? '' : 's'}`

const monthsBetween = (from: string, to: string): number => {
  const a = dayjs.utc(from, DATE_FORMAT)
  const b = dayjs.utc(to, DATE_FORMAT)
  return (b.year() - a.year()) * MONTHS_PER_YEAR + (b.month() - a.month())
}

// The gap two dates really have, in the unit the schedule was built with when it fits; null when none.
export const intervalBetween = (from: string, to: string, prefer: SeriesUnit): string | null => {
  if (!isDateText(from) || !isDateText(to)) return null
  const days = daysBetween(from, to)
  if (days <= 0) return null
  const months = monthsBetween(from, to)
  if (prefer === 'month' && months > 0 && addUnitClamped(from, 'month', months) === to) {
    return unitLabel(months, 'month')
  }
  if (prefer !== 'day' && days % WEEK_DAYS === 0) return unitLabel(days / WEEK_DAYS, 'week')
  return unitLabel(days, 'day')
}

// The statuses a Series visit can take, in OpenEMR's list order.
export const APPT_STATUS_OPTIONS: { value: string; label: string }[] = Object.entries(
  STATUS_LABELS,
).map(([value, label]) => ({ value, label }))

// OpenEMR default apptstat titles without the code; unknown codes show as written.
export const apptStatusLabel = (code: string | null): string =>
  code === null ? '—' : STATUS_LABELS[code] ?? code
